import { useState } from 'react';

interface Premio {
  id: string;
  tipo: 'produto' | 'xp' | 'voucher' | 'desconto';
  nome: string;
  valor: number;
  data: string;
  jogo: string;
  status: 'resgatado' | 'pendente' | 'expirado';
  imagem: string;
}

interface HistoricoPremiosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function HistoricoPremiosModal({ isOpen, onClose }: HistoricoPremiosModalProps) {
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');

  // Mock data - dados realistas de prêmios ganhos
  const premios: Premio[] = [
    {
      id: '1',
      tipo: 'produto',
      nome: 'Fone de Ouvido Bluetooth Premium',
      valor: 500,
      data: '2024-01-15',
      jogo: 'Roleta da Sorte',
      status: 'resgatado',
      imagem: 'https://readdy.ai/api/search-image?query=premium%20wireless%20bluetooth%20headphones%20with%20sleek%20modern%20design%20on%20white%20background%20product%20photography%20studio%20lighting&width=400&height=400&seq=premio1&orientation=squarish'
    },
    {
      id: '2',
      tipo: 'xp',
      nome: 'Bônus de XP',
      valor: 1000,
      data: '2024-01-14',
      jogo: 'Quiz de Produtos',
      status: 'resgatado',
      imagem: 'https://readdy.ai/api/search-image?query=golden%20star%20trophy%20award%20icon%20with%20sparkles%20on%20gradient%20background%20digital%20illustration&width=400&height=400&seq=premio2&orientation=squarish'
    },
    {
      id: '3',
      tipo: 'voucher',
      nome: 'Vale Compras R$ 100',
      valor: 100,
      data: '2024-01-12',
      jogo: 'Roleta da Sorte',
      status: 'pendente',
      imagem: 'https://readdy.ai/api/search-image?query=gift%20voucher%20card%20with%20ribbon%20on%20white%20background%20elegant%20design%20product%20photography&width=400&height=400&seq=premio3&orientation=squarish'
    },
    {
      id: '4',
      tipo: 'produto',
      nome: 'Smartwatch Fitness Tracker',
      valor: 800,
      data: '2024-01-10',
      jogo: 'Quiz de Produtos',
      status: 'resgatado',
      imagem: 'https://readdy.ai/api/search-image?query=modern%20smartwatch%20fitness%20tracker%20with%20black%20band%20on%20white%20background%20product%20photography%20studio%20lighting&width=400&height=400&seq=premio4&orientation=squarish'
    },
    {
      id: '5',
      tipo: 'xp',
      nome: 'Mega Bônus de XP',
      valor: 2500,
      data: '2024-01-08',
      jogo: 'Roleta da Sorte',
      status: 'resgatado',
      imagem: 'https://readdy.ai/api/search-image?query=golden%20star%20trophy%20award%20icon%20with%20sparkles%20on%20gradient%20background%20digital%20illustration&width=400&height=400&seq=premio5&orientation=squarish'
    },
    {
      id: '6',
      tipo: 'desconto',
      nome: 'Cupom 20% OFF',
      valor: 20,
      data: '2024-01-05',
      jogo: 'Quiz de Produtos',
      status: 'expirado',
      imagem: 'https://readdy.ai/api/search-image?query=discount%20coupon%20ticket%20with%20percentage%20symbol%20on%20white%20background%20modern%20design&width=400&height=400&seq=premio6&orientation=squarish'
    },
    {
      id: '7',
      tipo: 'produto',
      nome: 'Mochila Executiva Premium',
      valor: 350,
      data: '2024-01-03',
      jogo: 'Roleta da Sorte',
      status: 'resgatado',
      imagem: 'https://readdy.ai/api/search-image?query=premium%20black%20executive%20backpack%20laptop%20bag%20on%20white%20background%20product%20photography%20studio%20lighting&width=400&height=400&seq=premio7&orientation=squarish'
    },
    {
      id: '8',
      tipo: 'xp',
      nome: 'Bônus de XP',
      valor: 750,
      data: '2024-01-01',
      jogo: 'Quiz de Produtos',
      status: 'resgatado',
      imagem: 'https://readdy.ai/api/search-image?query=golden%20star%20trophy%20award%20icon%20with%20sparkles%20on%20gradient%20background%20digital%20illustration&width=400&height=400&seq=premio8&orientation=squarish'
    }
  ];

  // Filtrar prêmios
  const premiosFiltrados = premios.filter(premio => {
    const matchTipo = filtroTipo === 'todos' || premio.tipo === filtroTipo;
    const matchStatus = filtroStatus === 'todos' || premio.status === filtroStatus;
    return matchTipo && matchStatus;
  });

  // Calcular estatísticas
  const totalPremios = premios.length;
  const produtosFisicos = premios.filter(p => p.tipo === 'produto').length;
  const xpInvestidos = premios.filter(p => p.tipo === 'xp').reduce((acc, p) => acc + p.valor, 0);
  const valorTotal = premios.reduce((acc, p) => {
    if (p.tipo === 'produto' || p.tipo === 'voucher') return acc + p.valor;
    return acc;
  }, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resgatado': return 'text-green-600 bg-green-50';
      case 'pendente': return 'text-yellow-600 bg-yellow-50';
      case 'expirado': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'resgatado': return 'Resgatado';
      case 'pendente': return 'Pendente';
      case 'expirado': return 'Expirado';
      default: return status;
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'produto': return 'ri-gift-line';
      case 'xp': return 'ri-star-line';
      case 'voucher': return 'ri-coupon-line';
      case 'desconto': return 'ri-percent-line';
      default: return 'ri-gift-line';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-trophy-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Histórico de Prêmios</h2>
                <p className="text-purple-100 text-sm">Todos os prêmios que você ganhou</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-trophy-fill text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-100 text-xs">Total de Prêmios</p>
                  <p className="text-2xl font-bold">{totalPremios}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-gift-fill text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-100 text-xs">Produtos Físicos</p>
                  <p className="text-2xl font-bold">{produtosFisicos}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-star-fill text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-100 text-xs">XP Ganhos</p>
                  <p className="text-2xl font-bold">{xpInvestidos.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-fill text-xl"></i>
                </div>
                <div>
                  <p className="text-purple-100 text-xs">Valor Total</p>
                  <p className="text-2xl font-bold">R$ {valorTotal}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Prêmio</label>
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="todos">Todos os Tipos</option>
                <option value="produto">Produtos Físicos</option>
                <option value="xp">Bônus de XP</option>
                <option value="voucher">Vouchers</option>
                <option value="desconto">Descontos</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="todos">Todos os Status</option>
                <option value="resgatado">Resgatado</option>
                <option value="pendente">Pendente</option>
                <option value="expirado">Expirado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Prêmios */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-400px)]">
          {premiosFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-4xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-lg">Nenhum prêmio encontrado</p>
              <p className="text-gray-400 text-sm mt-2">Tente ajustar os filtros</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {premiosFiltrados.map((premio) => (
                <div
                  key={premio.id}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all"
                >
                  <div className="flex gap-4">
                    <div className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                      <img
                        src={premio.imagem}
                        alt={premio.nome}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">{premio.nome}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(premio.status)}`}>
                          {getStatusText(premio.status)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                          <i className={`${getTipoIcon(premio.tipo)} text-purple-600 text-sm`}></i>
                        </div>
                        <span className="text-sm text-gray-600">{premio.jogo}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ri-calendar-line text-gray-400 text-sm"></i>
                          <span className="text-xs text-gray-500">
                            {new Date(premio.data).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {premio.tipo !== 'desconto' && (
                          <div className="flex items-center gap-1">
                            {premio.tipo === 'xp' ? (
                              <>
                                <i className="ri-star-fill text-yellow-500 text-sm"></i>
                                <span className="text-sm font-bold text-gray-900">{premio.valor} XP</span>
                              </>
                            ) : (
                              <span className="text-sm font-bold text-green-600">R$ {premio.valor}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Mostrando <strong>{premiosFiltrados.length}</strong> de <strong>{totalPremios}</strong> prêmios
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
